import api from 'api';
import CheckFilled from 'assets/CheckFilled';
import CloseIcon from 'assets/CloseIcon';
import ThreeDotsIcon from 'assets/ThreeDotsIcon';
import classNames from 'classnames';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import MentionTextareaField from 'components/MentionTextareaField';
import Modal from 'components/Modal';
import FormModal from 'components/Modal/FormModal';
import SelectContent from 'components/Select/components/SelectContent';
import SelectFilterContent from 'components/Select/components/SelectFilterContent';
import SelectTrigger from 'components/Select/components/SelectTrigger';
import SelectField from 'components/SelectField';
import Tooltip from 'components/Tooltip';
import useSelect from 'hooks/useSelect';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Field } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { setNotifications } from 'store/actions/global.action';
import { selectPersonalId } from 'store/selectors/auth.selector';
import { selectNotifications } from 'store/selectors/global.selector';
import toastr from 'toastr';
import enums from 'utilities/enums';
import DateManager from 'utilities/services/DateManager';
import IconManager from 'utilities/services/IconManager';
import validators from 'utilities/services/validators';
import utils from '../../utils';
import './styles.scss';

const { ICON_BASED_ON_TYPE, NOTIFICATION_STATUSES, replacePlaceholders, NOTIFICATION_DELETION_STATUSES } = utils;

const { ENTITY_STATUSES, MAPPED_CASE_STATUSES, NOTIFICATION_TYPES } = enums;

const Notification = ({
  items: { activityLogs, headerItemForwardPath, headerItemName, title, notificationDeletionAction, hitDetailId },
  type,
  dateUpdated,
  status,
  id,
  closeNotificationsModal,
}) => {
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotifyModdalOpen, setIsNotifyModalOpen] = useState(false);
  const [isReassignModdalOpen, setIsReassignModalOpen] = useState(false);
  const threeDotsRef = useRef();
  const { data: notifications, unreadNotificationsCount, ...rest } = useSelect(selectNotifications) || [];
  const curentUserId = useSelect(selectPersonalId);

  const userOptions = useSelector(state =>
    state.global.usersToAssign
      .map(({ id: uId, name }) => ({
        label: name,
        value: uId,
        id: uId,
      }))
      .filter(({ id }) => id !== curentUserId),
  );

  const isLinkedEntityDeleted = useMemo(() => headerItemForwardPath === null, [headerItemForwardPath]);

  const changeStatus = useCallback(
    async (newStatus, showToaster) => {
      try {
        setIsStatusUpdating(true);
        await api.notifications.updateNotificationStatus({ id, notificationStatus: newStatus });
        // Update notifications
        const newNotifications = notifications
          .map(notification => {
            if (notification.id === id) {
              if (newStatus === NOTIFICATION_STATUSES.DELETE || newStatus === NOTIFICATION_STATUSES.ARCHIVE) {
                // Remove notification if the status is DELETE
                return null;
              }
              return { ...notification, status: newStatus };
            }
            return notification;
          })
          .filter(notification => notification !== null); // Filter out deleted notifications
        const isStatusUnread = status =>
          status == NOTIFICATION_STATUSES.UNREAD || status == NOTIFICATION_STATUSES.UNREAD_MANUALLY;
        const isStatusRead = status => status == NOTIFICATION_STATUSES.READ || status == NOTIFICATION_STATUSES.ARCHIVE;

        const isNewStatusUnread = isStatusUnread(newStatus);
        const isOldStatusUnread = isStatusUnread(status);
        const isNewStatusRead = isStatusRead(newStatus);
        const isOldStatusRead = isStatusRead(status);

        // Check if both statuses are unread or both are read
        const shouldChangeCount = !(isNewStatusUnread && isOldStatusUnread) && !(isNewStatusRead && isOldStatusRead);

        dispatch(
          setNotifications({
            unreadNotificationsCount: unreadNotificationsCount + (shouldChangeCount ? (isNewStatusUnread ? 1 : -1) : 0),
            data: newNotifications,
            ...rest,
          }),
        );
        showToaster && toastr.success('Successfully updated notification status');
        setIsDropdownOpen(false);
      } catch (err) {
        console.error(err);
      } finally {
        setIsStatusUpdating(false);
      }
    },
    [notifications, unreadNotificationsCount, rest, id],
  );

  const changeStatusAndCloseModal = useCallback(async () => {
    closeNotificationsModal && !isLinkedEntityDeleted && closeNotificationsModal();
    status !== NOTIFICATION_STATUSES.READ && changeStatus(NOTIFICATION_STATUSES.READ);
  }, [closeNotificationsModal, changeStatus, isLinkedEntityDeleted]);

  const navigeateToLinkedEntity = useCallback(
    elementId => {
      const targetPath = `/${headerItemForwardPath}`;
      if (location.pathname !== targetPath && !isLinkedEntityDeleted) {
        history.push(targetPath, { elementId });
      }
    },
    [history, headerItemForwardPath, isLinkedEntityDeleted],
  );

  const handleClick = useCallback(
    e => {
      e.stopPropagation();
      navigeateToLinkedEntity();
      changeStatusAndCloseModal();
    },
    [changeStatusAndCloseModal, navigeateToLinkedEntity],
  );

  const handleActivityLogClick = useCallback(
    elementId => e => {
      e.stopPropagation();
      changeStatusAndCloseModal();
      navigeateToLinkedEntity(elementId);
    },
    [navigeateToLinkedEntity, changeStatusAndCloseModal],
  );

  const handleDeleteActionClick = useCallback(
    notificationDeletionAction => async e => {
      try {
        e.stopPropagation();
        setIsStatusUpdating(true);

        if (type === NOTIFICATION_TYPES.CASE_DELETED) {
          await api.notifications.caseDeletionDecision(id, { notificationDeletionAction });
        } else {
          await api.notifications.entityDeletionDecision(id, { notificationDeletionAction });
        }
        changeStatus(NOTIFICATION_STATUSES.READ);
      } catch (err) {
        console.error(err);
      } finally {
        setIsStatusUpdating(false);
      }
    },
    [id],
  );

  const handleThreeDotsClick = useCallback(e => {
    e.stopPropagation();
    setIsDropdownOpen(old => !old);
  }, []);

  const handleMenuStatusClick = useCallback(
    (status, disabled) => e => {
      e.stopPropagation();
      !disabled && changeStatus(status, true);
      setIsDropdownOpen(false);
    },
    [],
  );

  const menuStatusOptions = useMemo(
    () => [
      {
        label: status === NOTIFICATION_STATUSES.READ ? 'Mark as Unread' : 'Mark as Read',
        onClick:
          status === NOTIFICATION_STATUSES.READ
            ? handleMenuStatusClick(NOTIFICATION_STATUSES.UNREAD)
            : handleMenuStatusClick(NOTIFICATION_STATUSES.READ),
      },
      { label: 'Archive', onClick: handleMenuStatusClick(NOTIFICATION_STATUSES.ARCHIVE) },
      { label: 'Delete', onClick: handleMenuStatusClick(NOTIFICATION_STATUSES.DELETE) },
    ],
    [handleMenuStatusClick, status],
  );

  const menuAdditionalOptions = useMemo(
    () => [
      {
        label: 'Notify...',
        onClick: e => {
          e.stopPropagation();
          if (type !== NOTIFICATION_TYPES.NOTIFY) {
            setIsNotifyModalOpen(true);
            setIsDropdownOpen(false);
          }
        },
        disabled: type === NOTIFICATION_TYPES.NOTIFY,
      },
      {
        label: 'Reassign...',
        onClick: e => {
          e.stopPropagation();
          setIsReassignModalOpen(true);
          setIsDropdownOpen(false);
        },
      },
    ],
    [type],
  );

  const properId = useMemo(() => {
    if (!headerItemForwardPath) return {};

    const pathElementsArray = headerItemForwardPath.split('/');

    const id = hitDetailId ? pathElementsArray[pathElementsArray.length - 3] : headerItemForwardPath.split('/').pop();
    const result = {
      ...(hitDetailId && { hitId: pathElementsArray[pathElementsArray.length - 1] }),
    };

    if (headerItemForwardPath.includes('case')) {
      return { ...result, complianceLogId: id };
    }

    if (headerItemForwardPath.includes('entities')) {
      return { entityId: id };
    }
    return {};
  }, [headerItemForwardPath, hitDetailId]);

  const KYCStatusOptions = useMemo(() => {
    return headerItemForwardPath?.includes('case')
      ? MAPPED_CASE_STATUSES
      : Object.entries(ENTITY_STATUSES).map(status => ({
          id: status[0],
          label: status[1],
          value: status[1],
        }));
  }, [headerItemForwardPath]);

  const handleReassignSubmit = useCallback(
    async values => {
      try {
        await api.notifications.reassign({ ...values, ...properId });

        setIsReassignModalOpen(false);
        toastr.success('Successfully reassigned');
      } catch (err) {
        console.error(err);
      }
    },
    [properId],
  );

  const handleNotifySubmit = useCallback(
    async ({ comment, ...values }) => {
      try {
        const { status } =
          !!comment.body &&
          (await api.comments.createNewComment({
            ...comment,
            ...properId,
          }));

        if (status === 200 || !comment.body) {
          await api.notifications.notifyUsers({
            ...values,
            ...properId,
            comment: comment?.body,
            complianceHitDetailsLogId: hitDetailId,
          });

          setIsNotifyModalOpen(false);
          toastr.success('Successfully notified');
        }
      } catch (err) {
        console.error(err);
      }
    },
    [properId, hitDetailId],
  );

  const classes = classNames('ickyc-notification', {
    'ickyc-notification--disabled': isStatusUpdating,
    'ickyc-notification--pointer': headerItemForwardPath !== null,
  });
  const menuItemClasses = useCallback(
    disabled =>
      classNames('ickyc-notification__menu-item', {
        'ickyc-notification__menu-item--disabled': disabled,
      }),
    [],
  );

  return (
    <div className={classes} onClick={handleClick}>
      <div className="ickyc-notification__header-box">
        <div className="ickyc-notification__dot">{status === NOTIFICATION_STATUSES.UNREAD && <span />}</div>
        <div className="ickyc-notification__header">
          {isLinkedEntityDeleted && (
            <Tooltip
              trigger={<span className="ickyc-notification__flag">{IconManager.get(IconManager.names.NO_ENTRY)}</span>}
              content={<span>Linked entity/case has been deleted</span>}
            />
          )}
          {ICON_BASED_ON_TYPE[type]}
          <p className="ickyc-notification__title">{replacePlaceholders(title, { headerItemName })}</p>

          <span className="ickyc-notification__date">{DateManager.formatRelativeTime(dateUpdated)}</span>

          <div className="ickyc-notification__three-dot-box">
            <ThreeDotsIcon onClick={handleThreeDotsClick} ref={threeDotsRef} />
            {isDropdownOpen && (
              <Modal
                relativeToParent={threeDotsRef?.current}
                hideModal={() => setIsDropdownOpen(false)}
                renderAsPortal={false}
              >
                <div>
                  <ul>
                    {menuStatusOptions.map(({ label, onClick }) => (
                      <li key={label} onClick={onClick} className="ickyc-notification__menu-item">
                        {label}
                      </li>
                    ))}
                  </ul>
                  {!isLinkedEntityDeleted && (
                    <ul>
                      {menuAdditionalOptions.map(({ label, disabled, onClick }) => (
                        <li key={label} onClick={onClick} className={menuItemClasses(disabled)}>
                          {label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Modal>
            )}
          </div>
        </div>
      </div>
      <div className="ickyc-notification__info">
        <div className="ickyc-notification__activity-items">
          {activityLogs.map(({ contentPlaceholder, id, commentId, isDeletedMention, ...rest }) => (
            <p key={id} className="ickyc-notification__activity-item" onClick={handleActivityLogClick(commentId)}>
              {replacePlaceholders(contentPlaceholder, { ...rest, headerItemName })}{' '}
              {commentId === null && !isLinkedEntityDeleted && (
                <span className="ickyc-notification__flag">[Deleted Comment]</span>
              )}
              {commentId && isDeletedMention && !isLinkedEntityDeleted && (
                <span className="ickyc-notification__flag">[Mention Removed]</span>
              )}
            </p>
          ))}
          {(type === NOTIFICATION_TYPES.ENTITY_DELETED || type === NOTIFICATION_TYPES.CASE_DELETED) &&
            notificationDeletionAction === NOTIFICATION_DELETION_STATUSES.PENDING_APPROVAL && (
              <div className="ickyc-notification__deletion-actions">
                <PrimaryButton variant="red" onClick={handleDeleteActionClick(NOTIFICATION_DELETION_STATUSES.REJECTED)}>
                  <CloseIcon />
                  Reject Deletion
                </PrimaryButton>
                <PrimaryButton onClick={handleDeleteActionClick(NOTIFICATION_DELETION_STATUSES.DELETED)}>
                  <CheckFilled />
                  Approve Deletion
                </PrimaryButton>
              </div>
            )}
        </div>
        {isNotifyModdalOpen && (
          <FormModal
            hideModal={() => setIsNotifyModalOpen(false)}
            title="Notify"
            className="ickyc-notification__additional-modal"
            onSubmit={handleNotifySubmit}
            showErrorsInConfirmation
            id="ickyc-notification__notify-modal"
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <Field
              label="Notify Users"
              name="notifiedUsersIds"
              component={SelectField}
              labelPosition="top"
              options={userOptions}
              multipleSelection
              Trigger={<SelectTrigger placeholder="Search Users" />}
              Content={<SelectFilterContent />}
              validate={validators.required()}
              required
            />
            <Field
              label={`Update ${headerItemForwardPath?.includes('case') ? 'case' : 'KYC'} status to`}
              name="status"
              component={SelectField}
              labelPosition="top"
              options={KYCStatusOptions}
              Trigger={<SelectTrigger placeholder="Choose Status" />}
              Content={<SelectContent />}
              validate={validators.required()}
              required
            />
            <Field label="Comment" name="comment" component={MentionTextareaField} labelPosition="top" />
          </FormModal>
        )}
        {isReassignModdalOpen && (
          <FormModal
            hideModal={() => setIsReassignModalOpen(false)}
            title="Reassign"
            className="ickyc-notification__additional-modal"
            onSubmit={handleReassignSubmit}
            showErrorsInConfirmation
            id="ickyc-notification__reassign-modal"
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <Field
              label="Reassign to User"
              name="notifiedUserId"
              component={SelectField}
              labelPosition="top"
              options={userOptions}
              Trigger={<SelectTrigger placeholder="Search Users" />}
              Content={<SelectFilterContent />}
              validate={validators.required()}
              required
            />
          </FormModal>
        )}
      </div>
    </div>
  );
};
export default Notification;
